import { ExclamationTriangleIcon } from '@heroicons/react/24/outline'
import { useMemo, useRef, useState } from 'react'
import { Bar } from 'react-chartjs-2'

import { useGetPluginPingsDetailsQuery } from '../../app/apiSliceCoreLogicApi'
import { Text } from '../../common/designs'
import { useDashboard } from '../../common/hooks'
import { ChartData } from '../../common/types/common'
import ConnectedComputersDrawer from './ConnectedComputersDrawer'
import ConnectedComputersPopover from './ConnectedComputersPopover'
import {
  getDayTimestampRange,
  getNotConnectedComputers,
  mapAndSortComputers,
} from './computerDetailsUtils'

interface Props {
  title: string
  data: ChartData | undefined
  expectedAgentAmount: number | null
  teamId?: string
  diffWithDate: string
  onDiffWithDateChange: (date: string) => void
}

const ConnectivityGraph = ({
  title,
  data,
  expectedAgentAmount,
  teamId,
  diffWithDate,
  onDiffWithDateChange,
}: Props) => {
  const { teams } = useDashboard()
  const [hoveredBar, setHoveredBar] = useState<{
    date: string
    count: number
    x: number
    y: number
  } | null>(null)
  const [isDrawerOpen, setIsDrawerOpen] = useState(false)
  const closeTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const annotationColor = 'hsla(239, 84%, 67%, 1)'
  const computerDataPoints = data?.datasets[0].data ?? [0]
  const { minComputers, avgComputers, maxComputers } = {
    minComputers: Math.min(...computerDataPoints),
    avgComputers: computerDataPoints.reduce((a, b) => a + b, 0) / computerDataPoints.length,
    maxComputers: Math.max(...computerDataPoints),
  }

  const activeDateQuery = useGetPluginPingsDetailsQuery(
    getDayTimestampRange(hoveredBar?.date ?? ''),
    { skip: !hoveredBar },
  )
  const diffDateQuery = useGetPluginPingsDetailsQuery(getDayTimestampRange(diffWithDate), {
    skip: !hoveredBar || !diffWithDate,
  })
  const connectedComputers = useMemo(
    () => mapAndSortComputers(activeDateQuery.data, teams, teamId),
    [activeDateQuery.data, teamId, teams],
  )
  const diffComputers = useMemo(
    () => mapAndSortComputers(diffDateQuery.data, teams, teamId),
    [diffDateQuery.data, teamId, teams],
  )
  const notConnectedComputers = useMemo(
    () => getNotConnectedComputers(connectedComputers, diffComputers),
    [connectedComputers, diffComputers],
  )
  const availableDates = data?.labels ?? []

  const cancelClose = () => {
    if (closeTimeoutRef.current) clearTimeout(closeTimeoutRef.current)
  }
  const scheduleClose = () => {
    cancelClose()
    closeTimeoutRef.current = setTimeout(() => {
      if (!isDrawerOpen) setHoveredBar(null)
    }, 150)
  }

  return (
    <div className='relative rounded-md bg-white p-4 shadow-md' onMouseLeave={scheduleClose}>
      <div className='flex justify-between'>
        <div className='mb-2 flex flex-col gap-4'>
          <Text>{title}</Text>

          {avgComputers === 0 ? (
            <div className='flex items-center gap-2 rounded-md border border-red-300 bg-red-50 px-1.5 py-1'>
              <ExclamationTriangleIcon className='size-5 text-red-600' />

              <Text size='xs' color='error' weight={500}>
                No computers have been detected!
              </Text>
            </div>
          ) : null}
        </div>

        {computerDataPoints.length ? (
          <>
            <div className='flex gap-8'>
              <Text className='mb-2' size='s'>
                Minimum: {minComputers}
              </Text>
              <Text className='mb-2' size='s'>
                Average: {Math.round(avgComputers * 10) / 10}
              </Text>
              <Text className='mb-2' size='s'>
                Maximum: {maxComputers}
              </Text>
            </div>
          </>
        ) : (
          <Text className='mb-2' size='s'>
            This team does not have any computers connected yet
          </Text>
        )}
      </div>

      {data && computerDataPoints.length ? (
        <Bar
          data={data}
          options={{
            onHover: (event, elements, chart) => {
              let index = elements[0]?.index

              if (index == null && typeof event.x === 'number') {
                if (
                  event.x < chart.chartArea.left ||
                  event.x > chart.chartArea.right ||
                  !data.labels.length
                ) {
                  scheduleClose()
                  return
                }

                const categoryIndex = chart.scales.x.getValueForPixel(event.x)
                if (categoryIndex == null) {
                  scheduleClose()
                  return
                }

                index = Math.round(categoryIndex)
              }

              if (index == null || index < 0 || index >= data.labels.length) {
                scheduleClose()
                return
              }

              cancelClose()
              const date = data.labels[index]
              const x = chart.scales.x.getPixelForValue(index)
              const y = elements[0]?.element.y ?? chart.chartArea.top
              setHoveredBar({
                date,
                count: Number(data.datasets[0].data[index]),
                x,
                y,
              })
            },
            scales: {
              y: {
                ticks: {
                  font: {
                    family: 'Inter',
                  },
                  stepSize: 1,
                },
                suggestedMax:
                  // Scale the graph by the biggest value found (either max computer or expected agent amount)
                  maxComputers > (expectedAgentAmount ?? 0)
                    ? maxComputers + 1
                    : (expectedAgentAmount ?? 0) + 1,
              },
            },
            plugins: {
              legend: {
                display: false,
              },
              tooltip: {
                enabled: false,
              },
              annotation: {
                annotations: expectedAgentAmount
                  ? {
                      line1: {
                        type: 'line',
                        yMin: expectedAgentAmount,
                        yMax: expectedAgentAmount,
                        borderColor: annotationColor,
                        borderWidth: 1,
                        label: {
                          content: 'Expected computer amount',
                          backgroundColor: annotationColor,
                          display: true,
                        },
                      },
                    }
                  : {},
              },
            },
          }}
          height='60px'
        />
      ) : null}

      {hoveredBar && !isDrawerOpen ? (
        <ConnectedComputersPopover
          title={title}
          activeDate={hoveredBar.date}
          connectedCount={hoveredBar.count}
          expectedCount={expectedAgentAmount}
          availableDates={availableDates}
          diffWithDate={diffWithDate}
          onDiffWithDateChange={onDiffWithDateChange}
          notConnectedComputers={notConnectedComputers}
          isLoading={activeDateQuery.isFetching || diffDateQuery.isFetching}
          position={hoveredBar}
          onMouseEnter={cancelClose}
          onMouseLeave={scheduleClose}
          onViewAllClick={() => setIsDrawerOpen(true)}
        />
      ) : null}

      {hoveredBar ? (
        <ConnectedComputersDrawer
          open={isDrawerOpen}
          onClose={() => {
            setIsDrawerOpen(false)
            setHoveredBar(null)
          }}
          title={title}
          activeDate={hoveredBar.date}
          availableDates={availableDates}
          diffWithDate={diffWithDate}
          onDiffWithDateChange={onDiffWithDateChange}
          connectedComputers={connectedComputers}
          notConnectedComputers={notConnectedComputers}
          isLoading={activeDateQuery.isFetching || diffDateQuery.isFetching}
        />
      ) : null}
    </div>
  )
}

export default ConnectivityGraph
